import { useEffect, useState } from 'react';
import { api } from '../../lib/api';
import type { AuditLog, LinkAnalytics } from '@shared/types';
import { MetricCard } from '../../components/Card';
import { Sparkline } from '../../components/Sparkline';
import { Spinner } from '../../components/Spinner';
import { formatNumber, formatRelative } from '../../lib/format';
import { IconBuilding, IconChart, IconLink, IconUsers } from '../../components/Icons';

interface SummaryResponse {
  metrics: { organizations: number; users: number; links: number; clicks: number };
  audit: AuditLog[];
  analytics: LinkAnalytics;
}

export function AdminDashboardPage() {
  const [data, setData] = useState<SummaryResponse | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    api
      .get<SummaryResponse>('/api/admin/summary')
      .then(setData)
      .catch((err) => setError(err instanceof Error ? err.message : String(err)));
  }, []);

  if (error)
    return (
      <div className="rounded-lg bg-red-50 dark:bg-red-950/40 text-red-700 dark:text-red-200 px-4 py-3 text-sm">
        {error}
      </div>
    );
  if (!data)
    return (
      <div className="flex items-center justify-center py-24">
        <Spinner size={28} />
      </div>
    );

  return (
    <div className="space-y-6">
      <header>
        <h1 className="text-2xl font-bold tracking-tight">Admin overview</h1>
        <p className="text-sm text-ink-500 dark:text-ink-400">
          Platform-wide metrics across all organizations.
        </p>
      </header>

      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <MetricCard
          label="Organizations"
          value={formatNumber(data.metrics.organizations)}
          icon={<IconBuilding />}
        />
        <MetricCard
          label="Users"
          value={formatNumber(data.metrics.users)}
          icon={<IconUsers />}
        />
        <MetricCard
          label="Links"
          value={formatNumber(data.metrics.links)}
          icon={<IconLink />}
        />
        <MetricCard
          label="Clicks"
          value={formatNumber(data.metrics.clicks)}
          icon={<IconChart />}
        />
      </div>

      <section className="card p-5">
        <h2 className="font-semibold mb-3">Click activity (30 days)</h2>
        <Sparkline
          data={data.analytics.timeseries.map((d) => ({
            date: d.date,
            total: d.totalClicks,
          }))}
          height={140}
          showAxis
        />
      </section>

      <section className="space-y-3">
        <h2 className="font-semibold">Recent audit events</h2>
        {data.audit.length === 0 ? (
          <p className="text-sm text-ink-500 dark:text-ink-400">No audit events yet.</p>
        ) : (
          <div className="table-wrap">
            <table className="data-table">
              <thead>
                <tr>
                  <th>When</th>
                  <th>Actor</th>
                  <th>Action</th>
                  <th>Target</th>
                </tr>
              </thead>
              <tbody>
                {data.audit.map((a) => (
                  <tr key={a.id}>
                    <td className="text-xs">{formatRelative(a.createdAt)}</td>
                    <td>{a.actorEmail ?? '—'}</td>
                    <td className="font-mono text-xs">{a.action}</td>
                    <td className="text-xs">
                      {a.targetType ? `${a.targetType}:${a.targetId ?? ''}` : '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </div>
  );
}
